import { ChatBoxContext, ChatSessionContext } from 'modules/chat/providers';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { EmptyPlaceholder } from 'shared/components/EmptyPlaceholder';
import { Loader } from 'shared/components/Loader';
import { useDispatch, useSelector } from 'react-redux';
import { SelectConversationEvent } from 'talkjs/types/talk.types';
import { sessionSelector } from 'app/store/session/selectors';
import { useStyles } from './Inbox.styles';
import { sessionReducers } from 'app/store/session/slice';

export const Inbox: FC = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [isMounted, setIsMounted] = useState(false);
  const inboxEl = useRef(null);
  const { talkLoaded, session } = useContext(ChatSessionContext);
  const { updateConversation, setParticipant } = useContext(ChatBoxContext);
  const isChatPinned = useSelector(sessionSelector.isChatPinned);
  const { classes } = useStyles();
  const isMobileView = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (talkLoaded && session) {
      const inbox = session.createInbox();
      inbox.select(null);
      inbox.mount(inboxEl?.current).then(() => setIsMounted(true));

      inbox.onSelectConversation((event: SelectConversationEvent) => {
        if (!event.conversation) {
          return;
        }

        event.preventDefault();

        if (event.conversation?.id) {
          setParticipant(event.others?.[0]);
          updateConversation(event.conversation?.id);
        }
      });
    }

    return () => setIsMounted(false);
  }, [session, setParticipant, talkLoaded, updateConversation]);

  const pinChat = useCallback(() => {
    dispatch(sessionReducers.setIsChatPinned(!isChatPinned));
  }, [dispatch, isChatPinned]);

  const buttonTitle = useMemo(() => (isChatPinned ? 'Unpin' : 'Pin'), [isChatPinned]);

  if (!session) {
    return (
      <Box display="flex" height="100%" justifyContent="center">
        <EmptyPlaceholder text="No active conversations yet." size={100} />
      </Box>
    );
  }

  return (
    <Box width="100%" height="100%">
      {!isMounted && <Loader />}
      {!isMobileView && (
        <Box className={classes.pinButton}>
          <Typography component="span" variant="subtitle2" onClick={pinChat}>
            {buttonTitle} Chat Window
          </Typography>
        </Box>
      )}
      <Box width="100%" height="100%" ref={inboxEl} />
    </Box>
  );
};
